"use client";
import Link from 'next/link';
import { animated, useSpring } from '@react-spring/web';
import { Home, ArrowLeft, Search } from 'lucide-react';

export default function NotFound() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center px-4">
      <div className="max-w-2xl w-full text-center">
        {/* Animated 404 */}
        <animated.div
          style={useSpring({
            from: { opacity: 0, transform: 'translateY(-20px)' },
            to: { opacity: 1, transform: 'translateY(0px)' },
            config: { duration: 600 }
          })}
          className="mb-8"
        >
          <h1 className="text-8xl md:text-9xl font-heading font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#97d700] to-[#7fb500] mb-4">
            404
          </h1>
          <div className="h-2 w-32 bg-gradient-to-r from-[#97d700] to-[#7fb500] rounded-full mx-auto"></div>
        </animated.div>

        {/* Error message */}
        <animated.div
          style={useSpring({
            from: { opacity: 0 },
            to: { opacity: 1 },
            delay: 200,
            config: { duration: 600 }
          })}
          className="mb-8"
        >
          <h2 className="text-2xl md:text-3xl font-heading uppercase tracking-wider text-foreground mb-4">
            Página no encontrada
          </h2>
          <p className="text-lg text-gray-600 max-w-md mx-auto">
            Lo sentimos, la página que buscas no existe o ha sido movida.
          </p>
        </animated.div>

        {/* Decorative element */}
        <animated.div
          style={useSpring({
            from: { opacity: 0, transform: 'scale(0.8)' },
            to: { opacity: 1, transform: 'scale(1)' },
            delay: 400,
            config: { duration: 600 }
          })}
          className="mb-12"
        >
          <div className="relative w-32 h-32 mx-auto">
            <div className="absolute inset-0 bg-[#97d700]/20 rounded-full animate-pulse"></div>
            <div className="absolute inset-4 bg-[#97d700]/30 rounded-full animate-pulse animation-delay-200"></div>
            <div className="absolute inset-8 bg-[#97d700]/40 rounded-full animate-pulse animation-delay-400"></div>
            <Search className="absolute inset-0 m-auto w-12 h-12 text-[#97d700]" />
          </div>
        </animated.div>

        {/* Action buttons */}
        <animated.div
          style={useSpring({
            from: { opacity: 0, transform: 'translateY(20px)' },
            to: { opacity: 1, transform: 'translateY(0px)' },
            delay: 600,
            config: { duration: 600 }
          })}
          className="space-y-4"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#97d700] text-black font-heading uppercase tracking-wider font-medium rounded-full shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
            >
              <Home className="w-5 h-5" />
              <span>Ir al inicio</span>
            </Link>

            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-foreground border-2 border-gray-200 font-heading uppercase tracking-wider font-medium rounded-full shadow-md hover:shadow-lg hover:border-[#97d700] transform hover:scale-[1.02] transition-all duration-300"
              aria-label="Volver a la página anterior"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Volver atrás</span>
            </button>
          </div>

          {/* Additional help text */}
          <p className="text-sm text-gray-500 mt-8">
            ¿Necesitas ayuda? Contáctanos en{' '}
            <a
              href="https://wa.me/34651062898?text=Hola,%20necesito%20ayuda%20con%20la%20página%20web"
              className="text-[#97d700] hover:underline font-medium"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contactar por WhatsApp para obtener ayuda (se abre en nueva pestaña)"
            >
              WhatsApp
            </a>
          </p>
        </animated.div>
      </div>

      {/* Background decoration */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#97d700]/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#7fb500]/5 rounded-full blur-3xl"></div>
      </div>
    </div>
  );
}
